// --------------------------------------------------
//  スケジュールの Day1 / Day2 タブ切替 (issue #33)
//
//  URL ハッシュ (#day1 / #day2) と連動させる。開催当日に
//  /2026/schedule/#day2 のように「今日のタブ」へ直リンクできるのが狙い。
//
//  - タブ切替時は pushState でハッシュだけ書き換える (スクロールさせない)
//  - ハッシュ付き URL で直接開いたらそのタブを開く
//  - ハッシュが無ければ、日本時間で開催当日の日のタブを開く
//    (パネルの data-schedule-date と照合。会期の前後は Day1)
//  - 戻る / 進む (popstate) でもタブが追随する
//  - パネルの表示制御は hidden 属性 (マークアップには書かず JS が付ける)。
//    NoJS ではどのパネルも隠れず 2 日分が縦に並ぶ (タブは CSS 側で隠す)
// --------------------------------------------------

const init = () => {
  const tablist = document.querySelector('[data-schedule-tabs]');
  if (!tablist) return;

  const tabs = Array.from(tablist.querySelectorAll('[data-schedule-tab]'));
  const panels = Array.from(document.querySelectorAll('[data-schedule-panel]'));
  if (tabs.length === 0 || panels.length === 0) return;

  // スプレッドシートの行固定のように、表の先頭が画面上端へ
  // 到達した間だけ日付 + ホール名の見出し行を固定中の見た目にする。
  // sticky の位置決め自体は CSS に任せ、ここでは透明な日付セルへ下地を
  // 足すための状態クラスだけを管理する。
  const tableWraps = Array.from(document.querySelectorAll('.schedule__table-wrap'));
  let stickyFrame = null;

  const updateStickyHeaders = () => {
    stickyFrame = null;

    tableWraps.forEach((wrap) => {
      const panel = wrap.closest('[data-schedule-panel]');
      const table = wrap.querySelector('.schedule-table');
      const header = table?.querySelector('thead');

      if (!table || !header || panel?.hidden) {
        wrap.classList.remove('is-header-stuck');
        return;
      }

      const tableRect = table.getBoundingClientRect();
      const headerHeight = header.getBoundingClientRect().height;
      const isStuck = tableRect.top <= 0 && tableRect.bottom > headerHeight;
      wrap.classList.toggle('is-header-stuck', isStuck);
    });
  };

  const requestStickyUpdate = () => {
    if (stickyFrame !== null) return;
    stickyFrame = window.requestAnimationFrame(updateStickyHeaders);
  };

  const dayFromHash = () => {
    const match = window.location.hash.match(/^#day(\d+)$/);
    if (!match) return null;
    const day = Number(match[1]);
    return panels.some((panel) => Number(panel.dataset.schedulePanel) === day) ? day : null;
  };

  // 会場 (渋谷) の日付で「今日」を決める。閲覧者の端末のタイムゾーンに左右されないように。
  // format() の出力形式 (並び順・区切り) は CLDR 次第で保証が無いので、
  // formatToParts() で年月日を個別に取り出して自前で YYYY-MM-DD に組む
  const dateParts = Object.fromEntries(
    new Intl.DateTimeFormat('en', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
      .formatToParts(new Date())
      .filter(({ type }) => ['year', 'month', 'day'].includes(type))
      .map(({ type, value }) => [type, value]),
  );
  const todayInTokyo = `${dateParts.year}-${dateParts.month}-${dateParts.day}`;
  const todayPanel = panels.find((panel) => panel.dataset.scheduleDate === todayInTokyo);
  const defaultDay = todayPanel ? Number(todayPanel.dataset.schedulePanel) : 1;

  const activate = (day) => {
    tabs.forEach((tab) => {
      const selected = Number(tab.dataset.scheduleTab) === day;
      tab.classList.toggle('is-active', selected);
      tab.setAttribute('aria-selected', String(selected));
      tab.setAttribute('tabindex', selected ? '0' : '-1');
    });
    panels.forEach((panel) => {
      panel.hidden = Number(panel.dataset.schedulePanel) !== day;
    });

    requestStickyUpdate();
  };

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const day = Number(tab.dataset.scheduleTab);
      activate(day);
      if (dayFromHash() === day) return;

      // location.hash への代入はページ内スクロールを起こすので pushState で書き換える
      const url = new URL(window.location);
      url.hash = `#day${day}`;
      window.history.pushState({}, '', url);
    });

    // roving tabindex (タブ UI のお作法): 左右キーで隣のタブへ
    tab.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      const index = tabs.indexOf(tab);
      const delta = event.key === 'ArrowRight' ? 1 : -1;
      const next = tabs[(index + delta + tabs.length) % tabs.length];
      next.focus();
      next.click();
    });
  });

  window.addEventListener('popstate', () => {
    activate(dayFromHash() ?? defaultDay);
  });

  window.addEventListener('scroll', requestStickyUpdate, { passive: true });
  window.addEventListener('resize', requestStickyUpdate);

  activate(dayFromHash() ?? defaultDay);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
